<template>
  <div v-if="cart" :ready="ready" :class="$mq" class="group fixed inset-0 z-[1503] overflow-hidden">
    <div class="absolute inset-0 overflow-hidden">
      <div
        @click="close"
        class="absolute inset-0 bg-blackest backdrop-blur-none transition-opacity duration-300 ease-out opacity-0 group-[[ready]]:opacity-70 group-[[ready]]:backdrop-blur"
      ></div>

      <div class="absolute inset-y-0 right-0 ml-10 max-w-full flex sm:ml-16">
        <div class="w-screen max-w-4xl">
          <div class="h-full">
            <div
              class="pt-6 h-full flex flex-col bg-white shadow-2xl transition-all duration-300 ease-out opacity-0 group-[[ready]]:opacity-100 translate-x-full group-[[ready]]:translate-x-0"
            >
              <div class="py-5 pl-8 pr-6 brand-bg sm:pl-12 sm:pr-8">
                <div class="flex items-center justify-between">
                  <h2 class="text-2xl font-bold text-blackest">Your Cart</h2>
                  <div class="ml-3 h-7 flex items-center">
                    <button type="button" class="focus:outline-none w-14 h-14 rounded-full bg-gray-100" @click="close">
                      <i class="fas fa-times fa-xl text-blackest"></i>
                    </button>
                  </div>
                </div>
                <DiscountProgressBar :cart="cart" class="mt-10" navbarMode />
              </div>

              <div class="mt-6 min-h-0 flex-1 flex flex-col overflow-y-auto">
                <div class="relative flex-1 px-8 sm:px-10">
                  <section
                    v-if="cart.items.length > 0"
                    class="pb-8 space-y-8"
                    :class="[{ tall: cart.items.length >= 2 }, $mq]"
                  >
                    <Item
                      v-for="item in cart.items"
                      :key="item.id"
                      :item="item"
                      :listing="item.listing"
                      :cart="cart"
                      :guestFavourites="guestFavourites"
                    />
                  </section>

                  <section v-else class="px-6 pt-4 pb-8 text-center">
                    <p class="text-lg font-semibold">You don't have anything in your cart!</p>
                    <a
                      href="/artworks"
                      class="mt-8 btn btn-primary text-base tracking-[2px] font-sans font-bold inline-block uppercase rounded"
                      >Start Browsing</a
                    >
                  </section>

                  <div class="mt-6">
                    <RecentViews :cart="cart" :checkoutPath="checkoutPath" :guestFavourites="guestFavourites" />
                  </div>
                </div>
              </div>

              <footer
                class="px-8 sm:px-10 pb-8 sm:pb-10 flex flex-col justify-evenly"
                :class="$mq === 'xs' ? 'border-1 border-t' : ''"
              >
                <div class="py-6">
                  <div class="space-y-4">
                    <div v-if="cart.internationalShipping.cents == 0" class="flex text-lg text-blackest">
                      <div>Shipping</div>
                      <span class="block ml-auto font-bold uppercase">Free</span>
                    </div>
                    <div v-else class="flex text-lg text-blackest">
                      <div>International Shipping</div>
                      <span class="block ml-auto font-bold">
                        {{ cart.internationalShipping.format }}
                      </span>
                    </div>
                    <h1 class="flex text-lg text-blackest">
                      <span>Total</span>
                      <span class="block ml-auto font-bold">{{ cart.total.format }}</span>
                    </h1>
                  </div>

                  <div class="mt-5 text-lg sm:text-sm" v-if="cart.discountApplied">
                    <div class="flex">
                      <span class="text-emerald-500 uppercase">You saved {{ cart.discounts.total.format }} 🎉</span>
                      <span class="block line-through text-red-600 ml-auto">{{ cart.subtotal.format }}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <a
                    :href="checkoutPath"
                    class="btn btn-primary w-full rounded font-sans tracking-[2px] text-base mt-2 py-6 uppercase font-bold"
                  >
                    Checkout
                  </a>
                </div>
                <!--
                  <div class="mt-4" v-if="$mq === 'xs'">
                    <Payment :key="paymentKey" />
                  </div>
                -->
              </footer>
            </div>
          </div>
        </div>
      </div>
    </div>

    <section
      class="hidden z-[1503] top-0 xs:top-auto bg-white w-full fixed left-0 bottom-0 sm:absolute sm:top-[45px] sm:-right-4 sm:left-auto sm:h-auto sm:bottom-auto sm:w-[380px] sm:shadow-[0_3px_10px_rgb(0,0,0,0.2)]"
      :class="$mq"
    >
      <section v-if="cart.processed || cart.items.length === 0" class="p-16 sm:p-6 text-center">
        <h1 class="mb-12 sm:mb-6 sm:text-xl">Your Cart is Empty</h1>
        <a href="/artworks" class="btn btn-primary py-6 sm:py-4 text-xl sm:text-base font-bold w-full block">
          Discover New Art
        </a>
      </section>
    </section>
  </div>
</template>

<script>
import Http from "@/lib/Http"
import Payment from "./Payment.vue"
import Item from "./Item.vue"
import RecentViews from "./RecentViews.vue"
import DiscountProgressBar from "./DiscountProgressBar.vue"
import { actions } from "./navStore.js"

export default {
  name: "Cart.ExpressCheckout",
  props: {
    cart: {
      type: Object,
      required: true,
    },
    checkoutPath: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    ready: false,
    paymentKey: Date.now(),
    guestFavourites: [],
  }),
  components: {
    Payment,
    Item,
    RecentViews,
    DiscountProgressBar,
  },
  watch: {
    cart: {
      deep: true,
      handler() {
        this.paymentKey = Date.now()
      },
    },
  },
  methods: {
    close() {
      this.ready = false
      setTimeout(() => {
        actions.close()
      }, 500)
    },
    async fetchGuestFavourites() {
      if (!window.config.userSignedIn) {
        const response = await Http.get("/api/guest_favourites", { headers: { Accept: "application/json" } })
        this.guestFavourites = response.data.guest_favourites || []
      }
    },
  },
  async mounted() {
    setTimeout(async () => {
      await this.fetchGuestFavourites()
      this.ready = true
    }, 1)
    window.addEventListener("turbo:before-visit", actions.close)
    window.addEventListener("popstate", actions.close)
  },
  destroyed() {
    window.removeEventListener("turbo:before-visit", actions.close)
    window.removeEventListener("popstate", actions.close)
  },
}
</script>
